import type { TranslationKey } from "@/lib/i18n/translations";

export type SiteConfig = typeof siteConfig;

/** Canonical production domain. */
export const PRIMARY_DOMAIN = "cagemdb.com";

/** Other domains serving the same site (should redirect to the primary). */
export const ALIAS_DOMAINS = ["nicolascage.io"] as const;

/**
 * Resolve the canonical site URL (no trailing slash):
 * explicit NEXT_PUBLIC_SITE_URL → production domain on production deploys →
 * the deployment's own URL on previews → localhost in development.
 */
export function resolveSiteUrl(env: {
  NEXT_PUBLIC_SITE_URL?: string;
  NEXT_PUBLIC_VERCEL_ENV?: string;
  NEXT_PUBLIC_VERCEL_URL?: string;
}): string {
  const explicit = env.NEXT_PUBLIC_SITE_URL?.trim();
  const url = explicit
    ? explicit
    : env.NEXT_PUBLIC_VERCEL_ENV === "production"
      ? `https://${PRIMARY_DOMAIN}`
      : env.NEXT_PUBLIC_VERCEL_URL
        ? `https://${env.NEXT_PUBLIC_VERCEL_URL}`
        : "http://localhost:3000";

  return url.replace(/\/+$/, "");
}

export interface NavItem {
  /** Path without the language prefix, e.g. "/movies" */
  href: string;
  labelKey: TranslationKey;
}

const navItems: NavItem[] = [
  { href: "/", labelKey: "nav.home" },
  { href: "/movies", labelKey: "nav.movies" },
];

export const siteConfig = {
  name: "Cage MDB",
  description:
    "The ultimate Nicolas Cage Movie Database - explore his complete filmography",
  /**
   * Canonical URL for metadata, canonical links, sitemaps and social cards.
   * Env vars are read one by one so Next.js can inline them in client bundles.
   */
  url: resolveSiteUrl({
    NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
    NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
    NEXT_PUBLIC_VERCEL_URL: process.env.NEXT_PUBLIC_VERCEL_URL,
  }),
  domains: {
    primary: PRIMARY_DOMAIN,
    aliases: ALIAS_DOMAINS,
  },
  /**
   * Public contact address shown in the privacy policy / terms.
   * Set NEXT_PUBLIC_CONTACT_EMAIL in production.
   */
  contactEmail: process.env.NEXT_PUBLIC_CONTACT_EMAIL ?? "",
  navItems,
  navMenuItems: navItems,
  // The source repository is private: never link to it from the site.
  links: {
    tmdb: "https://www.themoviedb.org/person/2963-nicolas-cage",
    imdb: "https://www.imdb.com/name/nm0000115/",
  },
};
